'use client';

import Script from 'next/script';
import { useEffect } from 'react';
import { useMentionMe, MENTION_ME_CONTAINER_ID, MENTION_ME_SCRIPT_ID } from 'hooks';
import './styles.css';
import { type MentionMeNode } from 'data-layer';

/**
 * MentionMeController
 *
 * React component responsible for initializing the Mention Me referral widget
 * using Next.js Script component and removing the skeleton placeholder.
 *
 * IMPORTANT:
 * - Uses Next.js Script component with "afterInteractive" strategy for optimal loading.
 * - Waits until user information is ready before rendering the script (via useMentionMe hook).
 * - Script loads only once per page lifecycle with the final user context.
 * - Uses MutationObserver to detect when MentionMe renders and removes skeleton.
 *
 * How it works:
 * - useMentionMe hook waits for user data and builds the script URL.
 * - Renders Next.js Script component with "afterInteractive" strategy when URL is ready.
 * - Uses MutationObserver to detect when MentionMe adds content to the container.
 * - Uses requestAnimationFrame to wait for content to be visually rendered.
 * - Removes the skeleton placeholder with a smooth fade transition.
 * - Does NOT re-initialize or update Mention Me after initial load.
 *
 * Architectural constraints (do not break):
 * - The Mention Me wrapper element (`#mmWrapper`) must already exist in the DOM.
 * - This component must remain a client component.
 * - The Mention Me script must be loaded only once per page lifecycle.
 *
 * Benefits of Next.js Script component:
 * - Automatic optimization by Next.js
 * - "afterInteractive" = loads after page is interactive (better UX)
 * - Better performance and prioritization
 * - More declarative React approach
 *
 * Rationale:
 * Mention Me is a legacy, load-once embed that does not support reactive
 * updates in SPA environments. This controller component isolates the
 * required imperative logic while leveraging Next.js optimizations.
 *
 * @param locale - Current locale used to build the Mention Me script URL.
 * @param config - MentionMe configuration from CMS (merchantId, hostDev, hostLive, journeyType)
 *
 * @returns Next.js Script component (conditionally rendered when URL is ready)
 */
export function MentionMeController({ locale, config }: { locale: string; config?: MentionMeNode }) {
  const scriptUrl = useMentionMe(locale, config);

  useEffect(() => {
    const container = document.getElementById(MENTION_ME_CONTAINER_ID);

    if (!container) {
      return;
    }

    let animationFrameId: number | null = null;

    /**
     * Checks if MentionMe content is actually visible and rendered in the DOM.
     *
     * This function performs two checks:
     * 1. Finds any child element that is NOT the skeleton (actual MentionMe content)
     * 2. Verifies the content has actual dimensions (height and width > 0)
     *
     * Why check dimensions?
     * - MentionMe might add DOM nodes before they're styled/rendered
     * - getBoundingClientRect() returns actual rendered dimensions
     * - Elements with display:none or not yet painted will have 0 dimensions
     *
     * @returns {boolean} true if MentionMe content exists and is visually rendered
     */
    const isMentionMeContentVisible = (): boolean => {
      // Find any child element that is NOT the skeleton
      // This will be the MentionMe widget content
      const mentionMeContent = Array.from(container.children).find(
        child => !child.classList.contains('mention-me-skeleton'),
      );

      if (!mentionMeContent) {
        return false;
      }

      // Check if the content has actual dimensions (is painted on screen)
      // getBoundingClientRect() gives us the actual rendered size
      const rect = mentionMeContent.getBoundingClientRect();

      // Only return true if element has been rendered with actual dimensions
      return rect.height > 0 && rect.width > 0;
    };

    /**
     * Removes the skeleton placeholder when MentionMe content is fully rendered.
     *
     * Flow:
     * 1. requestAnimationFrame waits for next browser paint cycle
     * 2. Check if MentionMe content is visible (has dimensions)
     * 3. If visible: Add fade-out class and wait for CSS transition
     * 4. If not visible: Recursively check again in next frame
     * 5. After transition ends: Remove skeleton from DOM
     *
     * Why requestAnimationFrame?
     * - Syncs with browser's repaint cycle (~60fps)
     * - Ensures DOM changes are painted before we check visibility
     * - More performant than setTimeout
     * - Automatically throttled when tab is inactive
     */
    const removeSkeleton = () => {
      const skeleton = container.querySelector('.mention-me-skeleton');

      if (!skeleton) {
        return;
      }

      /**
       * requestAnimationFrame ensures we check AFTER the browser has painted.
       * This is crucial because MentionMe might add DOM nodes before they're styled.
       */
      animationFrameId = requestAnimationFrame(() => {
        if (isMentionMeContentVisible()) {
          // Content is rendered! Start fade-out animation
          skeleton.classList.add('mention-me-skeleton--fade-out');

          /**
           * Wait for CSS transition to complete (300ms opacity fade)
           * before removing from DOM for smooth visual transition
           */
          const handleTransitionEnd = () => {
            skeleton.remove();
            skeleton.removeEventListener('transitionend', handleTransitionEnd);
          };

          skeleton.addEventListener('transitionend', handleTransitionEnd);
        } else {
          // Content not ready yet - check again in the next animation frame
          // This creates a recursive loop until content is visible
          animationFrameId = requestAnimationFrame(removeSkeleton);
        }
      });
    };

    /**
     * MutationObserver: Watch for DOM changes in the MentionMe container
     *
     * Why MutationObserver?
     * - MentionMe is a third-party script that directly manipulates the DOM
     * - We can't use React lifecycle methods to detect when it renders
     * - MutationObserver is a native browser API that watches for DOM changes
     * - It's event-driven (no polling) and highly performant
     *
     * What it watches:
     * - childList: true - watches for added/removed child nodes
     * - subtree: true - watches the entire subtree, not just direct children
     *
     * When MentionMe adds content:
     * 1. Observer detects the DOM change
     * 2. Callback fires and calls removeSkeleton()
     * 3. requestAnimationFrame ensures content is painted before checking
     */
    const observer = new MutationObserver(() => {
      removeSkeleton();
    });

    // Start watching the container for any child node additions/removals
    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    // Check immediately in case MentionMe already rendered before observer setup
    removeSkeleton();

    // Cleanup
    return () => {
      observer.disconnect();

      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return <>{scriptUrl && <Script id={MENTION_ME_SCRIPT_ID} src={scriptUrl} strategy="afterInteractive" />}</>;
}
